import React, { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import Video from '@components/VideoModule';
import { VIDEO_PLAY_BUTTON_2 } from '@constants';
import { PageSlice, SliceHowItWorksStepsDetails, SliceHowItWorksStepsItem } from '@contracts';
import Modal from './Modal';
import TextLinkButton from './TextLinkButton';
import { SFDBodyLG, SFDHeading2 } from './Typography';

interface Props {
  slice: PageSlice;
}

const HowItWorksSteps = ({ slice }: Props) => {
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SliceHowItWorksStepsDetails;
  const sliceTitle = t(sliceDetails.heading_text_key);
  const sliceItems = slice.items as SliceHowItWorksStepsItem[];
  const [modalData, setModalData] = useState({ open: false, videoPreviewUrl: '', videoUrl: '' });

  return (
    <Container>
      <TitleWrapper>
        <SFDHeading2>{sliceTitle}</SFDHeading2>
      </TitleWrapper>
      <ItemsWrapper>
        {sliceItems.map((sliceItem, index) => (
          <ItemWrapper key={`hiws-item-${sliceItem.main_text_key}`}>
            {sliceItem?.image?.url ? (
              <ItemImageWrapper>
                <LazyLoadImage src={sliceItem.image?.url} alt={sliceItem.image?.alt || ''} />
              </ItemImageWrapper>
            ) : null}
            <ItemTextWrapper>
              <StepWrapper>
                {t('components.step')} {index + 1}
              </StepWrapper>
              {sliceItem?.main_text_key ? (
                <MainTextWrapper>
                  <SFDBodyLG $bold>{t(sliceItem.main_text_key)}</SFDBodyLG>
                </MainTextWrapper>
              ) : null}
              {sliceItem?.info_text_key ? (
                <InfoTextWrapper>
                  <SFDBodyLG>{t(sliceItem.info_text_key)}</SFDBodyLG>
                </InfoTextWrapper>
              ) : null}
              {sliceItem?.video_preview_image?.url && sliceItem?.video_link_text_key && sliceItem?.video_url?.url ? (
                <VideoButtonWrapper
                  onClick={() =>
                    setModalData({
                      open: true,
                      videoPreviewUrl: sliceItem.video_preview_image?.url || '',
                      videoUrl: sliceItem.video_url?.url || ''
                    })
                  }
                >
                  <TextLinkButton $color={Colors.lilac}>{t(sliceItem.video_link_text_key)}</TextLinkButton>
                  <LazyLoadImage src={VIDEO_PLAY_BUTTON_2} alt={'video play'} />
                </VideoButtonWrapper>
              ) : null}
            </ItemTextWrapper>
          </ItemWrapper>
        ))}
      </ItemsWrapper>
      <Modal
        title={''}
        modalOpen={modalData.open}
        paddingDesktop="0"
        paddingMobile="0"
        onClose={() =>
          setModalData({
            open: false,
            videoPreviewUrl: '',
            videoUrl: ''
          })
        }
        width="580px"
      >
        <VideoWrapper>
          <Video mp4={modalData.videoUrl} autoplay={false} controls={true} muted={false} loop={false} />
        </VideoWrapper>
      </Modal>
    </Container>
  );
};

export default HowItWorksSteps;

const Container = styled.div`
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  padding: 40px 0;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const TitleWrapper = styled.div`
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
  text-align: center;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const ItemsWrapper = styled.div`
  display: flex;
  gap: 40px;
  display: flex;
  justify-content: center;
  align-self: center;

  @media (max-width: 1250px) {
    gap: 30px;
    flex-direction: column;
    max-width: 100%;
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  gap: 20px;
  min-width: 320px;
  max-width: 380px;
  flex-direction: column;

  @media (max-width: 1250px) {
    gap: 20px;
    min-width: 0;
  }
`;

const ItemImageWrapper = styled.div`
  border-radius: 2px;
  overflow: hidden;

  img {
    width: 100%;
    max-width: 100%;
  }
`;

const StepWrapper = styled.div`
  color: ${Colors.navyBlue};
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: BrownStd;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const MainTextWrapper = styled.div``;

const InfoTextWrapper = styled.div``;

const VideoButtonWrapper = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  cursor: pointer;
`;

const VideoWrapper = styled.div<{ $backgroundImage?: string }>`
  border-radius: 2px;
  background: ${Colors.sky};

  & .video-container {
    height: 100%;
    position: relative;
  }

  & .video-wrapper {
    padding-bottom: 68%;
  }
`;

const ItemTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
